// Mobile-optimized settings page
// Large touch-friendly controls

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  User,
  Check,
  RotateCcw,
  AlertTriangle
} from 'lucide-react'
import useSettingsStore from '../../stores/settingsStore'
import { db } from '../../db/database'

export default function MobileSettingsPage() {
  const navigate = useNavigate()

  const [surveyorName, setSurveyorName] = useState('')
  const [saved, setSaved] = useState(false)
  const [resetting, setResetting] = useState(false)

  const defaultSurveyor = useSettingsStore(state => state.defaultSurveyor)
  const loading = useSettingsStore(state => state.loading)
  const loadSettings = useSettingsStore(state => state.loadSettings)
  const setDefaultSurveyor = useSettingsStore(state => state.setDefaultSurveyor)

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  useEffect(() => {
    setSurveyorName(defaultSurveyor)
  }, [defaultSurveyor])

  const handleSaveSurveyor = async () => {
    try {
      await setDefaultSurveyor(surveyorName)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (error) {
      console.error('Failed to save surveyor name:', error)
    }
  }

  const handleResetDatabase = async () => {
    if (!window.confirm('Reset database? This will delete ALL surveys, photos and settings. The app will reload with fresh data.')) {
      return
    }

    setResetting(true)
    try {
      await db.delete()
      window.location.reload()
    } catch (error) {
      console.error('Failed to reset database:', error)
      alert('Failed to reset database. Please try clearing browser data manually.')
      setResetting(false)
    }
  }

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Header */}
      <div className="bg-slate-600 text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <button
          onClick={() => navigate('/')}
          className="p-2 -ml-2 hover:bg-slate-500 rounded-lg"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-semibold">Settings</h1>
      </div>

      {/* Settings content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Default Surveyor */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <User className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Default Surveyor</h2>
              <p className="text-sm text-slate-500">Pre-filled for new surveys</p>
            </div>
          </div>

          <input
            type="text"
            value={surveyorName}
            onChange={(e) => setSurveyorName(e.target.value)}
            placeholder="Enter your name"
            className="w-full px-4 py-3 text-lg border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-3"
          />

          <button
            onClick={handleSaveSurveyor}
            disabled={loading}
            className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
              saved
                ? 'bg-emerald-600 text-white'
                : 'bg-primary-600 text-white active:bg-primary-700'
            }`}
          >
            {saved ? (
              <>
                <Check className="w-5 h-5" />
                Saved!
              </>
            ) : (
              'Save Name'
            )}
          </button>
        </div>

        {/* About */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <h2 className="font-semibold text-slate-900 mb-3">About</h2>
          <div className="space-y-1 text-sm text-slate-600">
            <p><strong>Clystcore Surveyors</strong></p>
            <p>Property Survey Application</p>
            <p className="text-slate-400">Version 1.0.0</p>
          </div>
        </div>

        {/* Reset Database */}
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Reset Database</h2>
              <p className="text-sm text-slate-500">Delete all data</p>
            </div>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            This will permanently delete all surveys, photos, and settings. Use only if you need to start fresh.
          </p>
          <button
            onClick={handleResetDatabase}
            disabled={resetting}
            className="w-full py-3 bg-red-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 active:bg-red-700 disabled:opacity-50"
          >
            <RotateCcw className={`w-5 h-5 ${resetting ? 'animate-spin' : ''}`} />
            {resetting ? 'Resetting...' : 'Reset Database'}
          </button>
        </div>
      </div>
    </div>
  )
}
